<template>
  <div class="gm-step gm-step1">
    <section class="gs-wizard-card">
      <h2>How do you want to send it?</h2>
      <p>Pick a channel for this broadcast. You can change it later, but the message content starts over.</p>
      <div class="gm-choice-grid">
        <div
          v-for="(option, index) in radio_options"
          :key="index"
          class="gm-choice"
          @click="changeCampaignType()"
        >
          <gd-custom-radio
            v-model="localModel.sendoption"
            :option="option"
            value="localModel.sendoption"
          />
        </div>
      </div>
    </section>

    <section class="gs-wizard-card">
      <h2>{{ $t('frontend_groovemail_campaigns_wizard_broadcasts_campaign_info_title') }}</h2>
      <p>Name it so you can find it in your broadcast list, and file it under a category.</p>
      <div class="gm-fields gm-step1__fields">
        <gs-field :label="$t('frontend_groovemail_campaign_name')">
          <text-input
            class="gm-input"
            variant="base"
            v-model="localModel.name"
            :validator="$v.localModel.name"
            :placeholder="$t('frontend_groovemail_campains_show_name')"
          />
        </gs-field>
        <gs-field
          :label="$t('frontend_groovemail_campaign_tags')"
          :error="$v.localModel.campaign_tags.$error ? $t('error_broadcast_category_required') : ''"
        >
          <treeselect
            class="custom-treeselect treeselect-add-new"
            :class="{
              'error': $v.localModel.campaign_tags.$error,
            }"
            v-model="localModel.campaign_tags"
            :multiple="true"
            @select="campaignCatSelected"
            :closeOnSelect="true"
            :clearOnSelect="true"
            :clearable="false"
            :options="campaignCustomCategories"
            :placeholder="$t('frontend_groovemail_campaign_tags')"
            aria-errormessage="campaign_tags-error"
          />
        </gs-field>
      </div>
    </section>

    <gs-named-modal
      name="create-campaign-category-modal"
      width="600px"
      max-width="100%"
      :title="$t('frontend_groovemail_mail_tag_create_new_category')"
    >
      <div class="gm-modal-body">
        <BroadcastCategory
          @fetchBroadcastCategories="fetchBroadcastCategories"
          @assignBroadcastCat="assignBroadcastCat"
          @closeCreateBroadcastCatModal="closeCreateCampaignCatModal"
        />
      </div>
    </gs-named-modal>
  </div>
</template>

<script>
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import Notify from '@groovepages/gd-ui-kit/notify';
import { required, minLength, maxLength } from 'vuelidate/lib/validators';

import BroadcastCategory from '@/components/broadcast-category.vue';
import { userEmailSendLimit } from '@/components/user-plan-limit/checkUserEmailSendLimit.js';

export default {
  name: 'step1',
  props: {
    model: {
      required: true,
    },
    campignTags: {
      required: true,
    },
    integrations: {
      required: true,
    },
  },

  data() {
    return {
      changesMadeInStep: false,
      localModel: this.model,
    };
  },

  validations: {
    localModel: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(50),
      },

      campaign_tags: {
        required,
      },
    },
  },

  methods: {
    showLoader() {
      this.$emit('showLoader');
    },

    hideLoader() {
      this.$emit('hideLoader');
    },

    checkChangesMadeOnThisStep() {
      return this.changesMadeInStep;
    },

    async campaignCatSelected(node) {
      if (node.id === 'createCampaignCatOption1') {
        this.openCreateCampaignCatModal();
      }
    },

    openCreateCampaignCatModal() {
      this.$root.$emit('modal-open', 'create-campaign-category-modal');
    },

    closeCreateCampaignCatModal() {
      this.$root.$emit('modal-close', 'create-campaign-category-modal');
    },

    async fetchBroadcastCategories() {
      await this.$emit('fetchCampaignTags');
    },

    assignBroadcastCat(category) {
      if (category && category.data && category.data.id) {
        this.localModel.campaign_tags.push(category.data.id);
      }
    },

    validate() {
      this.$v.localModel.name.$touch();
      this.$v.localModel.campaign_tags.$touch();

      const isValid = !this.$v.localModel.name.$invalid && this.$v.localModel.name.$dirty && !this.$v.localModel.campaign_tags.$invalid;
      
      this.$emit('on-validate', isValid);
      return isValid;
    },

    async validateForSaveAndExit() {
      this.$v.localModel.name.$touch();
      return !this.$v.localModel.name.$invalid && this.$v.localModel.name.$dirty && !this.$v.localModel.campaign_tags.$invalid;
    },

    changeCampaignType() {
      if (this.localModel.campaignid) {
        Notify.warn(
          this.$t(
            'frontend_groovemail_campaigns_wizard_broadcasts_campaign_type_changed_warning'
          )
        );
        this.localModel.editor_type = 1;
        this.localModel.sender_id = null;
        this.localModel.smscontent = null;
        this.localModel.emailsubject = null;
        this.localModel.emailpreviewtext = null;
        this.localModel.emailcontent = '';
        this.localModel.emailContentSections = '';
        this.localModel.email_id = null;
        this.localModel.template_name = '';
        this.localModel.template_folder = null;
        this.localModel.postal_sequence_id = null;
      }
    },
  },

  components: {
    Treeselect,
    BroadcastCategory,
  },

  computed: {
    
    campaignCustomCategories() {
      const campaignCustomCategories = [];
      if (this.campignTags && this.campignTags.length) {
        this.campignTags.forEach((element) => {
          campaignCustomCategories.push({
            id: element.id,
            label: element.name,
          });
        });
      }
      const tagIndex = campaignCustomCategories.findIndex((tagEle) => {
        return tagEle.id === 'createCampaignCatOption1';
      });
      if (tagIndex < 0) {
        campaignCustomCategories.push({
          id: 'createCampaignCatOption1',
          label: this.$t('frontend_groovemail_mail_tag_create_new_category'),
        });
      }
      return campaignCustomCategories;
    },

    radio_options() {
      return [
        {
          id: 'email',
          title: this.$t(
            'frontend_groovemail_campaigns_wizard_broadcasts_campaign_send_option_email'
          ),
          description: this.$t(
            'frontend_groovemail_campaigns_wizard_broadcasts_campaign_send_option_email_desc'
          ),
          placeholder: '',
        },
        {
          id: 'sms',
          title: this.$t(
            'frontend_groovemail_campaigns_wizard_broadcasts_campaign_send_option_sms'
          ),
          description: this.$t(
            'frontend_groovemail_campaigns_wizard_broadcasts_campaign_send_option_sms_desc'
          ),
          placeholder: '',
        },
        {
          id: 'postal',
          title: this.$t(
            'frontend_groovemail_campaigns_wizard_broadcasts_campaign_send_option_postal'
          ),
          description: this.$t(
            'frontend_groovemail_campaigns_wizard_broadcasts_campaign_send_option_postal_desc'
          ),
          placeholder: '',
        },
      ];
    },
  },

  watch: {
    localModel() {
      this.$emit('updateModel', this.localModel);
    },

    'localModel.sendoption': async function () {
      this.changesMadeInStep = true;
      this.localModel.remainingEmails = await userEmailSendLimit(
        this.localModel.sendoption
      );
    },

    'localModel.name': function () {
      this.changesMadeInStep = true;
      this.$emit('stepValidity', !this.$v.localModel.name.$invalid, 'step1');
    },

    'localModel.campaign_tags.length': function () {
      this.changesMadeInStep = true;
      if (
        this.localModel.campaign_tags &&
        this.localModel.campaign_tags.length
      ) {
        const tagIndex = this.localModel.campaign_tags.findIndex((ele) => {
          return ele === 'createCampaignCatOption1';
        });
        if (tagIndex >= 0) {
          this.localModel.campaign_tags.splice(tagIndex, 1);
        }
      }
    },
  },
};
</script>

<style>
.gm-campaign-create .gm-step1__fields {
  max-width: 560px;
}
</style>
